import { ComponentPropsWithoutRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';

import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import { PRODUCT_TYPE_BOOK, SaleProduct } from '@v2/shared/lib/megaSale';
import { isNamedFormat } from '@v2/shared/lib/utils';
import { ProductTileState } from './useProductTile';

type Props = {
  tile: ProductTileState;
  product: SaleProduct;
  /** Distinguishes the tile's controls from the detail dialog's copy of them. */
  idSuffix?: string;
  onAdd: (skuCode: string, offerCode: string | null) => Promise<void>;
};

/**
 * The look of a picker on this page — the box, not what sits inside it.
 *
 * Stated once because the single-term control below has to be the *same* control, and two
 * class lists that merely agree today would not stay that way.
 *
 * The height is stated rather than padded to, and that is not a shortcut. Chrome's UA sheet
 * pins `line-height: normal !important` on a closed `<select>`, so the select's line box is
 * ~21px whatever the page says, while anything else inherits the page's 24px — identical
 * padding would draw two different boxes. WebKit goes further: while it is theming a
 * `<select>` it discards the author's padding outright, which is the standing explanation
 * for Safari drawing this dropdown shorter than the static control above it where Chrome
 * drew them alike. A declared 36px is an instruction neither engine reinterprets, so both
 * boxes are `h-9` and cannot drift apart whatever either does with the padding.
 */
const CONTROL_CLASSES =
  'w-full h-9 border-[1.5px] border-slate-300 rounded pl-2.5 font-medium text-slate-900 bg-white';

/**
 * A `<select>`: the shared box, room on the right for the chevron `Picker` draws, and the
 * states it reacts in.
 *
 * `appearance-none` is what makes the rest of it ours. A themed `<select>` draws its own
 * arrow at an inset the UA picks, and no author declaration moves it — measured: at
 * `padding-right` of 10px, 24px and 100px Chrome painted the arrow in the same pixel
 * column every time, its glyph ending 5px short of the control's outer edge, while the
 * same padding visibly moved where the label truncated. Dropping the native chrome is
 * therefore not a detour around the browser, it is the only way to place that chevron at
 * all. It also hands the box back: an unthemed `<select>` takes padding and height like
 * any other element, in every engine.
 */
const SELECT_CLASSES = `${CONTROL_CLASSES} appearance-none pr-8 hover:border-slate-400 focus:border-slate-900`;

/**
 * A term with no alternatives: the same control, minus the chevron and the interaction.
 *
 * It keeps the symmetric 10px on the right rather than the select's chevron allowance —
 * there is nothing there to make room for, and the terms on this sale already fill the
 * line ("1 Issue, July 2016 Issue" is 171px of the 171px available), so borrowing 22px
 * for a chevron it does not have would truncate text that fits today.
 *
 * Truncating rather than wrapping is what legacy did too: its static control carried the
 * dropdown's own `text-overflow: ellipsis`, so a long term is cut off in one line rather
 * than growing the tile. `min-w-0` is what lets the span shrink far enough to ellipsize —
 * a flex item will not go below its content width without it, and the text would spill
 * out past the border instead.
 */
const STATIC_CONTROL_CLASSES = `${CONTROL_CLASSES} pr-2.5 flex items-center`;

/**
 * A picker, chevron and all.
 *
 * The chevron is `shared/components/Select`'s, down to the icon, the colour and the inset:
 * that component draws `faAngleDown` in `text-slate-400` with `p-1` inside a `px-2.5`
 * button, so its glyph sits 14px in from the border. This is the same geometry, which is
 * the point — a native arrow crammed 5px from the edge was the one thing on this page that
 * did not look like a v2 dropdown.
 *
 * `pointer-events-none` so the chevron is not a dead spot: the whole control opens the
 * list, exactly as it did when the arrow was the browser's.
 */
function Picker({ children, ...props }: ComponentPropsWithoutRef<'select'>) {
  return (
    <div className="relative">
      <select className={SELECT_CLASSES} {...props}>
        {children}
      </select>
      <FontAwesomeIcon
        icon={faAngleDown}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400"
      />
    </div>
  );
}

/**
 * The Term and Type pickers, and the button.
 *
 * Native `<select>`s rather than `shared/components/Select`. This is the one place on
 * the page where that is the right call: a sale renders hundreds of these and the
 * Listbox is a HeadlessUI component per control, where a `<select>` is one element with
 * an options list the browser owns. On a phone it also gets the native picker, which is
 * what the customer expects for a term.
 *
 * The form does not submit anywhere. The legacy one POSTed to `/cart?xCmd=add` and let
 * a jQuery module intercept it; this calls the storefront api and stays on the page,
 * which is what lets the drawer open with the item already in it.
 */
export default function ProductForm({ tile, product, idSuffix = '', onAdd }: Props) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { merged, selectedTerm, selectedTypeId, setSelectedTermId, setSelectedTypeId, skuCode } = tile;

  const terms = merged.restricted_terms || [];
  const typeVariant = merged.variants.find((variant) => variant.name === 'Type');
  const otherVariants = merged.variants.filter(
    (variant) => variant.name !== 'Term' && variant.name !== 'Type' && (variant.options || []).length > 0
  );

  const typeSuffix = merged.attributes.is_subscription ? ' Subscription' : '';

  const add = async () => {
    if (!skuCode) {
      setError('This option is not available right now.');
      return;
    }

    setAdding(true);
    setError(null);

    try {
      await onAdd(skuCode, product.coupon_code ?? null);
    } catch (failure) {
      console.error('Could not add the product to the cart', failure);
      setError(failure instanceof Error ? failure.message : 'We could not add this to your cart.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-3">
      {terms.length > 1 ? (
        <>
          {/*
            A book's Term is its binding, not its length: the same dropdown offers
            "Paperback" and "Hardcover", so "Subscription length: Paperback" is what a
            screen reader was being told.
          */}
          <label className="sr-only" htmlFor={`term-${product.id}${idSuffix}`}>
            {product.product_type === PRODUCT_TYPE_BOOK ? 'Format' : 'Subscription length'}
          </label>
          <Picker
            id={`term-${product.id}${idSuffix}`}
            value={String(selectedTerm?.id ?? '')}
            onChange={(event) => setSelectedTermId(event.target.value)}
          >
            {terms.map((term) => (
              <option key={String(term.id)} value={String(term.id)}>
                {term.option}
                {term.extraSavings && term.extraSavings > 0 ? ` -- Save an extra ${term.extraSavings}%` : ''}
              </option>
            ))}
          </Picker>
        </>
      ) : (
        terms.length === 1 &&
        isNamedFormat(terms[0].option) && (
          <div className={STATIC_CONTROL_CLASSES}>
            <span className="truncate min-w-0">{terms[0].option}</span>
          </div>
        )
      )}

      {typeVariant && (typeVariant.options || []).length > 0 && (
        <>
          <label className="sr-only" htmlFor={`type-${product.id}${idSuffix}`}>
            Subscription type
          </label>
          <Picker
            id={`type-${product.id}${idSuffix}`}
            value={String(selectedTypeId ?? '')}
            onChange={(event) => setSelectedTypeId(event.target.value)}
          >
            {typeVariant.options.map((option) => {
              // A `new_only` product cannot be renewed, so the option is not offered.
              if (option.option !== 'New' && merged.attributes.new_only) {
                return null;
              }

              return (
                <option key={String(option.id)} value={String(option.id)}>
                  {option.option === 'New' ? `New${typeSuffix}` : 'Renew my Subscription'}
                </option>
              );
            })}
          </Picker>
        </>
      )}

      {otherVariants.map((variant) => (
        <div key={variant.name}>
          {!merged.merge_type_selectors && (
            <p className="m-0 mb-1 text-sm font-medium text-slate-600">{variant.title}:</p>
          )}
          <Picker aria-label={variant.title} defaultValue={String(variant.options[0]?.id ?? '')}>
            {variant.options.map((option) => (
              <option key={String(option.id)} value={String(option.id)}>
                {option.option}
                {variant.name.match(/^PackType/) && merged.attributes.is_subscription ? ' Subscription' : ''}
              </option>
            ))}
          </Picker>
        </div>
      ))}

      {/*
        Smaller than the page's default button, and told never to wrap.

        `size="sm"` rather than a `px-*` in className, because `cn` is plain clsx and does
        not resolve Tailwind conflicts: a padding passed alongside the default size's
        `px-8` loses or wins on the order Tailwind emits the two rules, not on the order
        they appear here, and `px-8` would have quietly kept winning. Changing the size
        swaps the class instead of racing it. The font size is safe to state directly —
        `sm` maps to `text-md`, which no config defines, so nothing competes with it.

        The default `px-8`/`text-lg` is sized for a call to action with a column to
        itself. Here there are two or three of these per row on a desktop and one beside a
        cover on a phone, and at 390px the label broke onto three lines inside a 115px
        button. `whitespace-nowrap` is the guarantee rather than the fix — the cover
        giving its width back is the fix — but it is what makes "never two lines" true at
        widths no one has measured.
      */}
      <Button
        onClick={add}
        isLoading={adding}
        loadingText="Adding..."
        fullWidth
        size="sm"
        disabled={!skuCode}
        className="whitespace-nowrap text-base"
      >
        Add to Cart
      </Button>

      {error && (
        <Alert color="danger" className="text-sm">
          {error}
        </Alert>
      )}
    </div>
  );
}
